import React, { useCallback, useEffect, useState } from 'react';
import { Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogProps, DialogTitle, Divider, FormControl, FormControlLabel, InputLabel, makeStyles, MenuItem, Select, Snackbar, Switch, TextField } from '@material-ui/core';
import { formatRelative } from 'date-fns'
import { fr } from 'date-fns/locale'
import { UserModel } from '../UsersTable';
import Fetcher, { URLBuild } from '../../services/fetcher';
import settings from '../../settings';
import { Alert } from '@material-ui/lab';
import { Role } from '../../contexts/Authentication';
import UserSub from '../../atoms/UserSub';
import ConnectAs from '../ConnectAs';

interface UserModalProps extends DialogProps {
  user?: UserModel
  updatable: boolean
  onRequestClose: (updated: boolean) => void
}

const useStyle = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'row'
  },
  remove: {
    position: "absolute",
    left: 16,
    backgroundColor: "#ff604f"
  },
  element: {
    marginTop: 16,
    marginBottom: 16
  },
  chip: {
    margin: 2,
    borderColor: 'darkgray'
  },
  addRoles: {
    display: 'flex',
    flex: 1,
    flexDirection: 'row',
    marginBottom: 10
  },
  selectRole: {
    flex: 5
  },
  roles: {
    display: 'flex',
    flex: 1,
    paddingBottom: 10
  },
  submitRole: {
    flex: 1
  },
  disabledInput: {
    marginTop: 16,
    marginBottom: 16,
    color: 'primary'
  }
})

const updatedRoles = [
  Role.Lecteur,
  Role.AbonneDecouverte,
  Role.AbonneLegacy,
  Role.Administrateur,
  Role.Chroniqueur,
  Role.Communaute,
  Role.Editorial1,
  Role.Contributeur,
  Role.Editorial2,
  Role.Redacteur,
  Role.RedacteurChef,
  Role.RedacteurJunior
];

const UserModal = (props: UserModalProps) => {
  const { user } = props;
  const classes = useStyle();

  const [email, setEmail] = useState(user?.email);
  const [slug, setSlug] = useState(user?.slug);
  const [emailActivated, setEmailActivated] = useState<boolean | undefined>(user?.emailActivated);
  const [legacyExpireReset, setLegacyExpireReset] = useState<boolean>(user?.legacyExpire ? true : false);
  const [roles, setRoles] = useState(user?.roles);
  const [selectedRole, setSelectedRole] = useState<string>('Reader');

  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const [customerId, setCustomerId] = useState<string | null | undefined>(undefined);

  useEffect(() => {
    if (user) {
      Fetcher.get(
        URLBuild(
          settings.PaymentProvider.paymentApi + '/stripe/customer/id/' + user.id,
          {})
      ).then((fetchedCusId: {customerId: string | null}) => {
        setCustomerId(fetchedCusId.customerId);
      }).catch((err) => {
        console.error(err);
        setCustomerId(null);
      })
    }
  }, [user])

  const remove = () => {
    setUpdating(true);
    Fetcher.delete(settings.OpenIdProvider.users + '/' + user?.id)
      .then(() => {
        setUpdating(false);
        if (props.updatable)
          props.onRequestClose(true);
      })
  }

  const update = () => {
    setUpdating(true);

    Fetcher.put(settings.OpenIdProvider.users + '/' + user?.id, JSON.stringify({
      email,
      slug,
      emailActivated,
      legacyExpireReset,
      roles
    }))
      .then(() => {
        setUpdating(false);
        if (props.updatable)
          props.onRequestClose(true);
      })
      .catch((err) => {
        try {
          err.json()
            .then((res: any) => {
              setError("Failed to update user: " + res.title);
              console.log(res);
            })
        } catch (error) {}
        setUpdating(false);
      });
  }

  const updateRoles = useCallback(() => {
    setRoles((prevRoles) => {
      if (prevRoles?.find(pr => pr === selectedRole))
        return prevRoles;
      return prevRoles ? [...prevRoles, selectedRole] : [selectedRole];
    });
  }, [setRoles, selectedRole]);

  const deleteRole = useCallback((role: string) => {
    setRoles((prevRoles) => prevRoles ? prevRoles?.filter(pr => pr !== role) : []);
  }, [setRoles]);

  return (
    <Dialog open={props.open} onClose={props.onClose} maxWidth="md">
      <DialogTitle>
        Utilisateur { user?.id } {email && <ConnectAs email={email}/>}
      </DialogTitle>
      <Divider />
      <DialogContent className={classes.container}>
          <div>
          <FormControl className={classes.roles}>
            {!props.updatable ? "Roles" : ""}
            { !!props.updatable &&
              <div className={classes.addRoles}>
                <InputLabel>Roles</InputLabel>
                <Select defaultValue={''} onChange={e => setSelectedRole(e.target.value as string)} className={classes.selectRole}>
                  { updatedRoles.map((r, i) => (
                    <MenuItem key={i} value={Role[r]}>{Role[r]}</MenuItem>
                  ))}
                </Select>
                <Button onClick={updateRoles} className={classes.submitRole}>+</Button>
              </div>
            }
            <div style={{ display: "flex", flexWrap: "wrap" }}>
              { roles && roles.map((role, i) => {
                if (!!props.updatable)
                  return (<Chip key={i} label={role} variant="outlined" onDelete={() => deleteRole(role)} className={classes.chip} />)
                return (<Chip key={i} label={role} variant="outlined" />)
              })}
            </div>
          </FormControl>
          <Divider />
          <div style={{ display: 'flex', flexDirection: "row" }}>
            <TextField
              autoFocus
              className={classes.element}
              style={{ width: 300 }}
              id="email"
              label="Adresse Email"
              type="email"
              onChange={(e) => setEmail(e.target.value)}
              defaultValue={user?.email}
              disabled={!props.updatable}
              fullWidth
            />
            <FormControlLabel
              control={<Switch color="primary" defaultChecked={user?.emailActivated} onChange={(_, res) => setEmailActivated(res)} />}
              label="Validé"
              labelPlacement="top"
              disabled={!props.updatable}
            />
          </div>
          <Divider />
          <TextField
              className={classes.element}
              id="slug"
              label="Slug"
              onChange={(e) => setSlug(e.target.value)}
              defaultValue={user?.slug ?? ''}
              fullWidth
              disabled={!props.updatable}
              color={"primary"}
            />
          <Divider />
            <div style={{ display: 'flex', flexDirection: "row" }}>
              <FormControlLabel
                style={{ marginLeft: '0' }}
                control={<Switch color="primary" defaultChecked={user?.legacyExpire ? true : false} onChange={(_, res) => setLegacyExpireReset(res)} />}
                label="Legacy"
                labelPlacement="top"
                disabled={!props.updatable}
                />
              { user?.legacyExpire &&
                <TextField
                  className={classes.element}
                  label="Legacy start date"
                  defaultValue={ formatRelative(new Date(user.legacyExpire), new Date(), { locale: fr }) }
                  fullWidth
                  disabled={true}
                  color={"primary"}
                />
              }
            </div>
        </div>
        {user !== undefined && !!customerId && <UserSub user={user} customerId={customerId as string}/>}
      </DialogContent>
      <DialogActions>
        { !!props.updatable ?
          (
            <>
              <Button onClick={remove} className={classes.remove}>Delete</Button>
              <Button onClick={() => props.onRequestClose(false)}>Cancel</Button>
              <Button onClick={update}>Update { updating && <CircularProgress size={24} /> }</Button>
            </>
          ) : (
            <>
              <Button onClick={() => props.onRequestClose(false)}>Quitter</Button>
            </>
          )
        }
      </DialogActions>
      <Snackbar open={error !== undefined} onClose={() => setError(undefined)} autoHideDuration={6000}>
        <Alert severity="error">{error}</Alert>
      </Snackbar>
    </Dialog>
  )
}

export default UserModal;